import { injectable } from 'inversify'

/**
 * The public half of a Web Push subscription, exactly the shape the backend's
 * `POST /me/push-subscriptions` takes (shared's `createPushSubscriptionSchema`).
 * The private key material never leaves the browser.
 */
export type PushSubscriptionJson = {
  endpoint: string
  keys: { p256dh: string; auth: string }
}

/**
 * Wraps the service worker registration, the Push API and the Notifications
 * permission (invariant 8: every browser API behind a gateway). Decides
 * nothing (CI-13) — whether to prompt, whether the server is configured,
 * what to show when blocked are all PushRepository's calls. This only
 * registers, subscribes, reads and unsubscribes.
 *
 * Ships with `FakeServiceWorkerGateway` (same file-pair convention as every
 * other gateway) whose behaviour is fully settable from a test.
 */
@injectable()
export class ServiceWorkerGateway {
  /** True only where all three pieces exist — a browser missing any one cannot do push at all. */
  isSupported(): boolean {
    return (
      typeof navigator !== 'undefined' &&
      'serviceWorker' in navigator &&
      typeof window !== 'undefined' &&
      'PushManager' in window &&
      'Notification' in window
    )
  }

  /** 'default' (never asked), 'granted', or 'denied'. 'denied' can only be undone by the user in browser settings. */
  permission(): NotificationPermission {
    return this.isSupported() ? Notification.permission : 'denied'
  }

  /**
   * Just the service-worker API — not `PushManager`/`Notification`, which
   * `isSupported()` additionally needs. A browser that lacks push can still
   * run the offline shell, so registration gates on this weaker predicate.
   */
  isRegistrationSupported(): boolean {
    return typeof navigator !== 'undefined' && 'serviceWorker' in navigator
  }

  /**
   * Idempotent — the browser returns the existing registration if this exact
   * script URL is already controlling the scope. `version` rides on the query
   * string because the worker is not bundled and cannot see Vite's define; a
   * deploy changes the URL, so the browser installs a fresh worker rather than
   * byte-comparing the old one.
   */
  async ensureRegistered(version: string): Promise<void> {
    if (!this.isRegistrationSupported()) return
    await navigator.serviceWorker.register(`/sw.js?v=${encodeURIComponent(version)}`)
  }

  async requestPermission(): Promise<NotificationPermission> {
    if (!this.isSupported()) return 'denied'
    return Notification.requestPermission()
  }

  /** The subscription this browser already holds for this app, if any — used to reflect "already on" without re-subscribing. */
  async currentSubscription(): Promise<PushSubscriptionJson | null> {
    if (!this.isSupported()) return null
    const registration = await navigator.serviceWorker.ready
    const existing = await registration.pushManager.getSubscription()
    return existing ? toJson(existing) : null
  }

  /**
   * Subscribes this browser to push for `vapidPublicKey` and returns the
   * public subscription. `null` if the browser handed back a subscription
   * missing its keys (older Safari has done this) — the caller treats that
   * as a failure rather than sending a half-subscription to the server.
   */
  async subscribe(vapidPublicKey: string): Promise<PushSubscriptionJson | null> {
    if (!this.isSupported()) return null
    const registration = await navigator.serviceWorker.ready
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      // Every current browser accepts the VAPID key as its base64url string
      // here; the older Uint8Array-only form is no longer needed.
      applicationServerKey: vapidPublicKey,
    })
    const json = toJson(subscription)
    return json.keys.p256dh !== '' && json.keys.auth !== '' ? json : null
  }

  async unsubscribe(): Promise<void> {
    if (!this.isSupported()) return
    const registration = await navigator.serviceWorker.ready
    const existing = await registration.pushManager.getSubscription()
    if (existing) await existing.unsubscribe()
  }
}

function toJson(subscription: PushSubscription): PushSubscriptionJson {
  const json = subscription.toJSON()
  return {
    endpoint: json.endpoint ?? '',
    keys: { p256dh: json.keys?.p256dh ?? '', auth: json.keys?.auth ?? '' },
  }
}
